import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AUTH_CHANGED_EVENT,
  bootstrapSession,
  clearAuth,
  getAccessToken,
  getRefreshToken,
  getUser,
  type CustomerUserInfo,
} from '../lib/auth';
import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context';
import { isEmbedded, notifyAuthSuccess } from '../lib/embed';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [user, setUser] = useState<CustomerUserInfo | null>(getUser());
  const lastNotifiedUserIdRef = useRef<string | null>(null);

  const clearLocalSessionState = useCallback(() => {
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const syncSessionFromStorage = useCallback(() => {
    const hasSession = Boolean(getAccessToken() || getRefreshToken());
    if (!hasSession) {
      clearLocalSessionState();
      return true;
    }

    return false;
  }, [clearLocalSessionState]);

  const hydrateSession = useCallback(async () => {
    setStatus('bootstrapping');

    const nextUser = await bootstrapSession();
    if (nextUser) {
      setUser(nextUser);
      setStatus('authenticated');
      return;
    }

    clearAuth({ emit: false });
    clearLocalSessionState();
  }, [clearLocalSessionState]);

  useEffect(() => {
    const bootstrapTimer = window.setTimeout(() => {
      void hydrateSession();
    }, 0);

    const handleAuthChanged = () => {
      if (syncSessionFromStorage()) {
        return;
      }
      void hydrateSession();
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key.startsWith('helpdesk_customer_')) {
        if (syncSessionFromStorage()) {
          return;
        }
        void hydrateSession();
      }
    };

    window.addEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.clearTimeout(bootstrapTimer);
      window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
      window.removeEventListener('storage', handleStorage);
    };
  }, [hydrateSession, syncSessionFromStorage]);

  useEffect(() => {
    if (status === 'authenticated' && user) {
      if (lastNotifiedUserIdRef.current !== user.id) {
        if (isEmbedded()) {
          notifyAuthSuccess(user);
        }
        lastNotifiedUserIdRef.current = user.id;
      }
      return;
    }

    lastNotifiedUserIdRef.current = null;
  }, [status, user]);

  const value = useMemo<AuthContextValue>(() => ({ status, user }), [status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
